import { ReactElement } from 'react';
import {
  LambdaCapacityProvider,
  LambdaCreateCapacityProviderRequest,
  LambdaUpdateCapacityProviderRequest,
} from '@localstack/types';
import { AwsClientOverrides, getSchema } from '@localstack/services';
import { DEFAULT_IAM_ROUTES, DEFAULT_KMS_ROUTES } from '@localstack/constants';

import { MagicForm } from '../../magic/MagicForm';
import { RelatedResourcePicker } from '../../../form';

export interface LambdaCapacityProviderFormProps {
  name: Optional<string>;
  data?: Optional<Partial<LambdaCapacityProvider>>;
  loading?: boolean;
  formId?: string;
  onCreate: (data: LambdaCreateCapacityProviderRequest) => unknown;
  onUpdate: (data: LambdaUpdateCapacityProviderRequest) => unknown;
  clientOverrides?: AwsClientOverrides;
}

export const LambdaCapacityProviderForm = ({
  data,
  name,
  loading,
  formId,
  onCreate,
  onUpdate,
  clientOverrides,
}: LambdaCapacityProviderFormProps): ReactElement => (
  <MagicForm
    data={data}
    schema={getSchema('Lambda')}
    loading={loading}
    entry={data ? 'UpdateCapacityProviderRequest' : 'CreateCapacityProviderRequest'}
    formId={formId}
    fieldConditions={{
      '^CapacityProviderName': !name,
    }}
    externalFields={{
      '^PermissionsConfig.CapacityProviderOperatorRoleArn$': (control, fieldName, required) => (
        <RelatedResourcePicker
          control={control}
          client="IAM"
          method="listRoles"
          arrayKeyName="Roles"
          property="Arn"
          fieldName={fieldName}
          entityName="IAM Role"
          creationRoute={DEFAULT_IAM_ROUTES.RESOURCES_IAM_ROLE_CREATE}
          required={required}
          clientOverrides={clientOverrides}
        />
      ),
      '^KmsKeyArn$': (control, fieldName, required) => (
        <RelatedResourcePicker
          control={control}
          client="KMS"
          method="listKeys"
          arrayKeyName="Keys"
          property="KeyArn"
          fieldName={fieldName}
          entityName="KMS Key"
          creationRoute={DEFAULT_KMS_ROUTES.RESOURCES_KMS_KEY_CREATE}
          required={required}
          clientOverrides={clientOverrides}
        />
      ),
    }}
    onSubmit={(d: LambdaCreateCapacityProviderRequest | LambdaUpdateCapacityProviderRequest) => {
      if (!data) return onCreate(d as LambdaCreateCapacityProviderRequest);
      return onUpdate({ ...d, CapacityProviderName: name } as LambdaUpdateCapacityProviderRequest);
    }}
  />
);
